/* eslint-disable @stylistic/max-len */
import type { ApiTonWalletVersion } from './api/chains/ton/types';
import type {
  ApiBaseCurrency,
  ApiChain,
  ApiLiquidStakingState,
  ApiNetwork,
  ApiNftMarketplace,
  ApiNominatorsStakingState,
  ApiSwapAsset,
  ApiSwapDexLabel,
  ApiToken,
} from './api/types';
import type { TOKEN_CARD_COLORS } from './components/main/helpers/cardColors';
import type { AutolockValueType, LangCode, LangItem } from './global/types';

export const APP_ENV = process.env.APP_ENV || 'production';
export const DEFAULT_NETWORK: ApiNetwork = process.env.DEFAULT_NETWORK === 'testnet' ? 'testnet' : 'mainnet';

export const IS_CORE_WALLET = process.env.IS_CORE_WALLET === '1';
export const IS_GRAM_WALLET = process.env.IS_GRAM_WALLET === '1';
// Both flags together form the wallet.ton.org combo build: Gram branding over Core behavior.
// Brand-axis code must check IS_GRAM_WALLET first, then IS_TON_BRAND; behavior/storage code keeps using IS_CORE_WALLET.
export const IS_TON_BRAND = IS_CORE_WALLET && !IS_GRAM_WALLET;
export const IS_LEGENDS_WALLET = !IS_CORE_WALLET && !IS_GRAM_WALLET;
// The third brand. Cards, MYCOIN vesting and the tips channel are My Wallet products that neither the Gram nor the
// TON Wallet brand carries, so they hang off this axis rather than off the identity or feature ones (Air agrees).
export const IS_MY_WALLET_BRAND = false;
// The trimmed-down product is the legacy TON Wallet (extension and the pre-Gram web app): no swaps, staking,
// ramps, multi-account, Ledger, BIP39 or locale choice. Gram Wallet Web keeps Core identity (storage key, jsbridge,
// domain) but ships the full feature set, so feature gates must check this axis, never IS_CORE_WALLET.
export const IS_FEATURE_LIMITED = IS_TON_BRAND;
export const NO_AGENT_AND_EXPLORE = IS_LEGENDS_WALLET;
export const NO_APP_INSTALL_PROMO = IS_LEGENDS_WALLET;
export const NO_HELP_CENTER = IS_LEGENDS_WALLET;
export const NO_NFT = IS_LEGENDS_WALLET;
export const NO_ACCOUNT_CONFIG = IS_LEGENDS_WALLET || process.env.NO_ACCOUNT_CONFIG === '1';
export const NO_REFERRER = IS_LEGENDS_WALLET || process.env.NO_REFERRER === '1';
export const APP_NAME = process.env.APP_NAME
  || (IS_GRAM_WALLET ? 'Gram Wallet' : IS_TON_BRAND ? 'TON Wallet' : 'Legends Wallet');
export const APP_VERSION = process.env.APP_VERSION!;
export const APP_COMMIT_HASH = process.env.APP_COMMIT_HASH!;
export const APP_ENV_MARKER = APP_ENV === 'staging' ? 'Beta' : APP_ENV === 'development' ? 'Dev' : undefined;
export const EXTENSION_NAME = IS_TON_BRAND ? 'TON Wallet' : 'Legends Wallet';
export const EXTENSION_DESCRIPTION = IS_TON_BRAND
  ? 'Set up your own TON Wallet on The Open Network'
  : 'Self-custodial TRON wallet for TRX and USDT TRC-20.';

export const DEBUG = APP_ENV !== 'production' && APP_ENV !== 'perf' && APP_ENV !== 'test';
export const DEBUG_MORE = false;
export const DEBUG_API = false;
export const DEBUG_VIEW_ACCOUNTS = false;
export const TEST_MNEMONIC = process.env.TEST_MNEMONIC?.trim();
export const TEST_PASSWORD = process.env.TEST_PASSWORD || 'test';

export const IS_PRODUCTION = APP_ENV === 'production';
export const IS_STAGING = APP_ENV === 'staging';
export const IS_TEST = APP_ENV === 'test';
export const IS_PERF = APP_ENV === 'perf';
export const IS_EXTENSION = process.env.IS_EXTENSION === '1';
export const IS_FIREFOX_EXTENSION = process.env.IS_FIREFOX_EXTENSION === '1';
export const IS_OPERA_EXTENSION = process.env.IS_OPERA_EXTENSION === '1';
export const IS_PACKAGED_ELECTRON = process.env.IS_PACKAGED_ELECTRON === '1';
export const IS_ANDROID_DIRECT = process.env.IS_ANDROID_DIRECT === '1';
export const IS_AIR_APP = process.env.IS_AIR_APP === '1';
export const IS_TELEGRAM_APP = process.env.IS_TELEGRAM_APP === '1';
export const IS_EXPLORER = process.env.IS_EXPLORER === '1';
export const IS_HEADLESS = process.env.IS_HEADLESS === '1';

export const ELECTRON_HOST_URL = 'https://dumb-host';
export const INACTIVE_MARKER = '[Inactive]';
export const PRODUCTION_URL = IS_CORE_WALLET
  ? 'https://wallet.ton.org'
  : IS_LEGENDS_WALLET ? 'https://wallet.legends.energy' : 'https://web.mywallet.io';
export const BETA_URL = IS_CORE_WALLET
  ? 'https://beta.wallet.ton.org'
  : IS_LEGENDS_WALLET ? 'https://wallet-beta.legends.energy' : 'https://beta.mywallet.io';
// Beta desktop auto-update feed base. This is BOTH the staging gate poll base and the value baked
// into app-update.yml by the generic electron-builder provider - the two must agree.
export const BETA_UPDATE_URL = 'https://s3.mywallet.io/public/desktop-beta';
// The pre-rebrand host still serves this very build - it is an extra domain of the same site, kept alive because
// outdated desktop clients poll it for update manifests. Listed explicitly rather than derived by negating
// PRODUCTION_URL, which would also match self-hosted installations.
export const LEGACY_APP_HOSTS: string[] = [];
// Where a legacy-host visitor is nudged to continue on the current brand. Opened via a plain anchor or `window.open`,
// never `openUrl`: `SUBPROJECT_URL_MASK` treats every `*.mywallet.io` host as a subproject, so `openUrl` would append
// the wallet context (addresses included) and open it in the in-app iframe browser - where the site renders blank
// under `X-Frame-Options: Deny`. `utm_source` attributes the migrated traffic.
export const NEW_APP_URL = `${PRODUCTION_URL}?utm_source=legacy_web`;
export const APP_INSTALL_URL = IS_GRAM_WALLET
  ? 'https://get.gramwallet.io/'
  : IS_LEGENDS_WALLET ? PRODUCTION_URL : 'https://get.mywallet.io/';
export const APP_REPO_URL = 'https://github.com/yooud/legends-wallet';
export const SELF_UNIVERSAL_HOST_URL = PRODUCTION_URL;
export const APP_WEBSITE_URL = IS_GRAM_WALLET
  ? 'https://gramwallet.io'
  : IS_LEGENDS_WALLET ? 'https://legends.energy' : 'https://mywallet.io';
export const TRANSACTION_EXPLORER_HOST_URL = IS_LEGENDS_WALLET
  ? APP_ENV === 'development' ? 'https://energy.yo' : APP_WEBSITE_URL
  : SELF_UNIVERSAL_HOST_URL;
export const APP_ICON_URL = IS_GRAM_WALLET
  ? 'https://gramwallet.io/icon-512x512.png'
  : `${PRODUCTION_URL}/logo.svg`;

// GitHub workflow uses an empty string as the default value if it's not in repository variables, so we cannot define a default value here
export const BASE_URL = process.env.BASE_URL || PRODUCTION_URL;

export const BOT_USERNAME = process.env.BOT_USERNAME || 'LegendsEnergy_bot';

export const SWAP_FEE_ADDRESS = process.env.SWAP_FEE_ADDRESS || 'UQDUkQbpTVIgt7v66-JTFR-3-eXRFz_4V66F-Ufn6vOg0GOp';
export const DIESEL_ADDRESS = process.env.DIESEL_ADDRESS || 'UQC9lQOaEHC6YASiJJ2NrKEOlITMMQmc8j0_iZEHy-4sl3tG';

export const STRICTERDOM_ENABLED = DEBUG && !IS_PACKAGED_ELECTRON;

export const DEBUG_ALERT_MSG = 'Shoot!\nSomething went wrong, please see the error details in Dev Tools Console.';

export const PIN_LENGTH = 4;

/** If true, legacy auth data (mnemonicEncrypted, authConfig) will be removed after migration to Enclave */
export const SHOULD_CLEANUP_LEGACY_AUTH = false;
export const NATIVE_BIOMETRICS_PROMPT_KEY = 'confirm an action in Legends Wallet';

/**
 * If `true`, a wallet created by this build gets a TON-specific mnemonic, which can never derive a foreign address.
 * Generation only: importing a BIP39 phrase works on every build. Those two used to be the same flag, which made
 * the restriction a trap - a phrase minted by a fuller build of the same product would have become unimportable
 * here the moment that build was rolled back to this one.
 */
export const SHOULD_GENERATE_TON_MNEMONIC = IS_FEATURE_LIMITED;

export const MNEMONIC_COUNT = 24;
// A TON-native build mints 24-word phrases, so it offers 24 first while still accepting the 12-word BIP39 ones a
// rollback might have to restore; the multichain builds lead with 12, matching what they mint.
export const MNEMONIC_COUNTS = SHOULD_GENERATE_TON_MNEMONIC ? [24, 12] : [12, 24];

export const PRIVATE_KEY_HEX_LENGTH = 64;
export const MNEMONIC_CHECK_COUNT = 3;

export const MOBILE_SCREEN_MAX_WIDTH = 700; // px

export const VIEW_TRANSITION_CLASS_NAME = 'active-view-transition';

export const ANIMATION_END_DELAY = 50;

export const ANIMATED_STICKER_TINY_ICON_PX = 16;
export const ANIMATED_STICKER_ICON_PX = 30;
export const ANIMATED_STICKER_TINY_SIZE_PX = 70;
export const ANIMATED_STICKER_SMALL_SIZE_PX = 110;
export const ANIMATED_STICKER_MIDDLE_SIZE_PX = 120;
export const ANIMATED_STICKER_DEFAULT_PX = 150;
export const ANIMATED_STICKER_BIG_SIZE_PX = 156;
export const ANIMATED_STICKER_HUGE_SIZE_PX = 192;

export const DEFAULT_PORTRAIT_WINDOW_SIZE = { width: 368, height: 770 };
export const DEFAULT_LANDSCAPE_WINDOW_SIZE = { width: 980, height: 788 };
export const TRANSACTION_ADDRESS_SHIFT = 4;

export const WHOLE_PART_DELIMITER = ' '; // https://www.compart.com/en/unicode/U+202F

export const DEFAULT_SLIPPAGE_VALUE = 5;

export const GLOBAL_STATE_CACHE_DISABLED = false;
export const GLOBAL_STATE_CACHE_KEY = IS_CORE_WALLET
  ? 'tonwallet-global-state'
  : IS_EXPLORER
    ? 'explorer-global-state'
    : IS_LEGENDS_WALLET ? 'legends-wallet-global-state' : 'mytonwallet-global-state';

export const ANIMATION_LEVEL_MIN = 0;
export const ANIMATION_LEVEL_MED = 1;
export const ANIMATION_LEVEL_MAX = 2;
export const ANIMATION_LEVEL_DEFAULT = ANIMATION_LEVEL_MAX;
export const THEME_DEFAULT = 'system';

export const MAIN_ACCOUNT_ID = '0-ton-mainnet';
export const TEMPORARY_ACCOUNT_NAME = 'Wallet';

export const TONCENTER_MAINNET_URL = process.env.TONCENTER_MAINNET_URL || 'https://toncenter.mytonwallet.org';
export const TONCENTER_MAINNET_KEY = process.env.TONCENTER_MAINNET_KEY;
export const ELECTRON_TONCENTER_MAINNET_KEY = process.env.ELECTRON_TONCENTER_MAINNET_KEY;
export const TONAPIIO_MAINNET_URL = process.env.TONAPIIO_MAINNET_URL || 'https://tonapiio.mytonwallet.org';

export const TONCENTER_TESTNET_URL = process.env.TONCENTER_TESTNET_URL || 'https://toncenter-testnet.mytonwallet.org';
export const TONCENTER_TESTNET_KEY = process.env.TONCENTER_TESTNET_KEY;
export const ELECTRON_TONCENTER_TESTNET_KEY = process.env.ELECTRON_TONCENTER_TESTNET_KEY;
export const TONAPIIO_TESTNET_URL = process.env.TONAPIIO_TESTNET_URL || 'https://tonapiio-testnet.mytonwallet.org';

export const BRILLIANT_API_BASE_URL = process.env.BRILLIANT_API_BASE_URL
  || (IS_LEGENDS_WALLET ? 'https://wallet-api.legends.energy' : 'https://api.mywallet.io');
export const PROXY_API_BASE_URL = process.env.PROXY_API_BASE_URL || `${BRILLIANT_API_BASE_URL}/proxy`;
export const IPFS_GATEWAY_BASE_URL = 'https://ipfs.io/ipfs/';
export const SSE_BRIDGE_URL = 'https://tonconnectbridge.mytonwallet.org/bridge/';

export const TON_CONNECT_ANALYTICS_URL = 'https://analytics.ton.org';

export const WALLET_CONNECT_BRIDGE_PATTERNS = 'https://*.walletconnect.com https://*.walletconnect.org wss://*.walletconnect.com wss://*.walletconnect.org';

/** WalletConnect Pay API + collect iframe (multi-level subdomains; not covered by `*.walletconnect.com`). */
export const WALLET_CONNECT_PAY_CONNECT_ORIGINS = [
  'https://api.pay.walletconnect.com/',
  'https://api.pay.walletconnect.org/',
  'https://staging.api.pay.walletconnect.org/',
  'https://pay.walletconnect.com/',
];

export const WALLET_CONNECT_PAY_FRAME_ORIGINS = [
  'https://pay.walletconnect.com/',
];

export const WALLET_CONNECT_PROJECT_ID = process.env.WALLET_CONNECT_PROJECT_ID || '';
export const WALLET_CONNECT_PAY_APP_ID = process.env.WALLET_CONNECT_PAY_APP_ID || '';

export const TRON_MAINNET_API_URL = process.env.TRON_MAINNET_API_URL
  || (IS_LEGENDS_WALLET ? 'https://node.legends.energy' : 'https://tronapi.mytonwallet.org');
export const TRON_TESTNET_API_URL = process.env.TRON_TESTNET_API_URL
  || (IS_LEGENDS_WALLET ? `${BRILLIANT_API_BASE_URL}/testnet` : 'https://api.shasta.trongrid.io');
export const TRON_MAINNET_HISTORY_API_URL = process.env.TRON_MAINNET_HISTORY_API_URL
  || (IS_LEGENDS_WALLET ? 'https://api.trongrid.io' : TRON_MAINNET_API_URL);
export const TRON_MAINNET_HISTORY_API_KEY = process.env.TRON_MAINNET_HISTORY_API_KEY || '';
export const TRON_TESTNET_HISTORY_API_URL = process.env.TRON_TESTNET_HISTORY_API_URL
  || (IS_LEGENDS_WALLET ? 'https://nile.trongrid.io' : TRON_TESTNET_API_URL);
export const TRON_TESTNET_HISTORY_API_KEY = process.env.TRON_TESTNET_HISTORY_API_KEY || '';

export const SOLANA_MAINNET_RPC_URL = process.env.SOLANA_MAINNET_RPC_URL || 'https://solanaapi.mytonwallet.org';
export const SOLANA_MAINNET_API_KEY = process.env.SOLANA_MAINNET_API_KEY;
export const SOLANA_TESTNET_RPC_URL = process.env.SOLANA_TESTNET_RPC_URL || 'https://solanaapi-devnet.mytonwallet.org';
export const SOLANA_TESTNET_API_KEY = process.env.SOLANA_TESTNET_API_KEY;
export const SOLANA_TESTNET_API_URL = process.env.SOLANA_TESTNET_API_URL || 'https://solanaapi-devnet.mytonwallet.org';
export const SOLANA_MAINNET_API_URL = process.env.SOLANA_MAINNET_API_URL || 'https://solanaapi.mytonwallet.org';

export const SOLANA_GASLESS_PAYER_ADDRESS = process.env.SOLANA_GASLESS_PAYER_ADDRESS || 'BkVfRKjZnnYCcRBgXBsfaWFZFidBL9drm5MZwNqoNGCu';

export const EVM_MAINNET_RPC_URL = process.env.EVM_MAINNET_RPC_URL || 'https://evmapi.mytonwallet.org';
export const EVM_TESTNET_RPC_URL = process.env.EVM_TESTNET_RPC_URL || 'https://evmapi-testnet.mytonwallet.org';

export const FRACTION_DIGITS = 9;
export const SHORT_FRACTION_DIGITS = 2;

export const MAX_PUSH_NOTIFICATIONS_ACCOUNT_COUNT = 3;

export const SUPPORT_USERNAME = 'LegendsEnergy_bot';
export const MW_NEWS_CHANNEL_NAME: Partial<Record<LangCode, string>> = {
  en: 'MyWalletEng',
  ru: 'MyWalletRus',
};
export const MW_TIPS_CHANNEL_NAME: Partial<Record<LangCode, string>> = {
  en: 'MyTonWalletTips',
  ru: 'MyTonWalletTipsRu',
};
export const NFT_MARKETPLACE_TITLES: Record<ApiNftMarketplace, string> = {
  getgems: 'Getgems',
  fragment: 'Fragment',
  opensea: 'OpenSea',
};
export const MW_STATIC_BASE_URL = 'https://static.mytonwallet.org';
export const MW_CARDS_BASE_URL = `${MW_STATIC_BASE_URL}/cards/v2/cards/`;
export const MW_CARDS_MINT_BASE_URL = `${MW_STATIC_BASE_URL}/mint-cards/`;
// Every outbound link the app puts in front of a user follows its brand. The blog stays on the My Wallet domain for
// all brands where it is visible, since that is the only place it is published (Air links it the same way).
export const APP_PROMO_URL = IS_GRAM_WALLET
  ? 'https://gramwallet.io/'
  : IS_LEGENDS_WALLET ? 'https://legends.energy/' : 'https://mywallet.io/';
export const APP_WEBSITE_HOST = IS_GRAM_WALLET
  ? 'gramwallet.io'
  : IS_LEGENDS_WALLET ? 'legends.energy' : 'mywallet.io';
export const APP_TERMS_OF_USE_URL = IS_GRAM_WALLET
  ? 'https://gramwallet.io/terms-of-use/'
  : IS_LEGENDS_WALLET ? 'https://legends.energy/terms-of-use' : 'https://mywallet.io/terms-of-use';
export const APP_PRIVACY_POLICY_URL = IS_GRAM_WALLET
  ? 'https://gramwallet.io/privacy-policy/'
  : IS_LEGENDS_WALLET ? 'https://legends.energy/privacy-policy' : 'https://mywallet.io/privacy-policy';
export const MY_WALLET_BLOG: Partial<Record<LangCode, string>> = {
  en: 'https://mywallet.io/en/blog/',
  ru: 'https://mywallet.io/ru/blog/',
};

export const MULTISEND_DAPP_URL = process.env.MULTISEND_DAPP_URL || 'https://multisend.mywallet.io/';
export const PORTFOLIO_DAPP_URL = process.env.PORTFOLIO_DAPP_URL || 'https://portfolio.mywallet.io/';
export const PORTFOLIO_API_URL = process.env.PORTFOLIO_API_URL || 'https://api-portfolio.mywallet.io/api';
export const AGENT_API_URL = process.env.AGENT_API_URL || 'https://agent.mywallet.io/api';

export const NFT_MARKETPLACE_URL = 'https://opensea.io/';
export const NFT_MARKETPLACE_TITLE = NFT_MARKETPLACE_TITLES.opensea;
export const TON_NFT_MARKETPLACE_URL = 'https://getgems.io/';
export const TON_NFT_MARKETPLACE_TITLE = NFT_MARKETPLACE_TITLES.getgems;
export const GETGEMS_BASE_MAINNET_URL = 'https://getgems.io/';
export const GETGEMS_BASE_TESTNET_URL = 'https://testnet.getgems.io/';
export const EMPTY_HASH_VALUE = 'NOHASH';

export const IFRAME_WHITELIST = [
  'http://localhost:*',
  'https://tonscan.org',
  'https://testnet.tonscan.org',
  'https://tonviewer.com',
  'https://testnet.tonviewer.com',
];
export const SUBPROJECT_URL_MASK = 'https://*.mywallet.io';

export const CEX_WAITING_DEADLINE = 3 * 60 * 60 * 1000; // 3 hours

export const PROXY_HOSTS = process.env.PROXY_HOSTS;

export const TINY_TRANSFER_MAX_COST = 0.01;

export const IMAGE_CACHE_NAME = IS_EXPLORER ? 'explorer-image' : 'legends-wallet-image';
// Bump this when locale keys change so cached packs do not fall back to English.
export const LANG_CACHE_NAME = 'legends-wallet-lang-18';

export const LANG_LIST: LangItem[] = [{
  langCode: 'en',
  name: 'English',
  nativeName: 'English',
  rtl: false,
}, {
  langCode: 'es',
  name: 'Spanish',
  nativeName: 'Español',
  rtl: false,
}, {
  langCode: 'ru',
  name: 'Russian',
  nativeName: 'Русский',
  rtl: false,
}, {
  langCode: 'zh-Hans',
  name: 'Chinese (Simplified)',
  nativeName: '简体',
  rtl: false,
}, {
  langCode: 'zh-Hant',
  name: 'Chinese (Traditional)',
  nativeName: '繁體',
  rtl: false,
}, {
  langCode: 'tr',
  name: 'Turkish',
  nativeName: 'Türkçe',
  rtl: false,
}, {
  langCode: 'de',
  name: 'German',
  nativeName: 'Deutsch',
  rtl: false,
}, {
  langCode: 'th',
  name: 'Thai',
  nativeName: 'ไทย',
  rtl: false,
}, {
  langCode: 'uk',
  name: 'Ukrainian',
  nativeName: 'Українська',
  rtl: false,
}, {
  langCode: 'pl',
  name: 'Polish',
  nativeName: 'Polski',
  rtl: false,
}, {
  langCode: 'ar',
  name: 'Arabic',
  nativeName: 'العربية',
  rtl: true,
}, {
  langCode: 'fa',
  name: 'Persian',
  nativeName: 'فارسی',
  rtl: true,
}];

export const IS_STAKING_DISABLED = IS_FEATURE_LIMITED;

// Blacklist-style feature flags (default unset = feature ON). Each is substituted at build time by
// `EnvironmentPlugin`, so it both drives Webpack dead-code elimination (drops code + npm deps) and is
// readable at runtime to silence behaviour/network for anything still bundled.
export const IS_TRON_ONLY = process.env.IS_TRON_ONLY === undefined
  ? IS_LEGENDS_WALLET
  : process.env.IS_TRON_ONLY === '1';
export const NO_PRICE_CHART = IS_LEGENDS_WALLET || process.env.NO_PRICE_CHART === '1';
export const NO_TON = IS_TRON_ONLY || process.env.NO_TON === '1';
export const NO_TRON = process.env.NO_TRON === '1';
export const NO_SOLANA = IS_TRON_ONLY || process.env.NO_SOLANA === '1';
export const NO_EVM = IS_TRON_ONLY || process.env.NO_EVM === '1';
export const NO_WALLETCONNECT = IS_TRON_ONLY || process.env.NO_WALLETCONNECT === '1';
export const NO_SWAP = IS_TRON_ONLY || process.env.NO_SWAP === '1';
export const NO_STAKING = IS_TRON_ONLY || process.env.NO_STAKING === '1';
export const NO_PORTFOLIO = IS_TRON_ONLY || process.env.NO_PORTFOLIO === '1';
export const NO_MFA = IS_TRON_ONLY || process.env.NO_MFA === '1';
export const NO_LEDGER = IS_TRON_ONLY || process.env.NO_LEDGER === '1';
export const NO_NOTIFICATIONS = process.env.NO_NOTIFICATIONS === '1';
export const VALIDATION_PERIOD_MS = 65_536_000; // 18.2 h.
export const ONE_TON = 1_000_000_000n;
export const DEFAULT_FEE = 15_000_000n; // 0.015 TON
export const UNSTAKE_TON_GRACE_PERIOD = 20 * 60 * 1000; // 20 m.

const LEGACY_NOMINATORS_STAKING_POOL = 'Ef8dgIOIRyCLU0NEvF8TD6Me3wrbrkS1z3Gpjk3ppd8m8-s_';
const DEFAULT_NOMINATORS_STAKING_POOL = 'Ef84o4VJRnlp1wsqSHov1QttqSTQda2Z1vGK-b7EaPQoeJMx';

// Must include every pool the backend can return in `nominatorsPool.address`, decommissioned ones
// included (accounts with a legacy stake still need to see and unstake it): builds without the
// STAKING_POOLS env var (e.g. the wallet.ton.org deploy) rely solely on this list, and an unknown
// address makes `fetchBackendStakingState` throw, silently killing staking polling for the account.
const DEFAULT_STAKING_POOLS = [
  LEGACY_NOMINATORS_STAKING_POOL,
  'Ef-WMmizoLk4CvqTKs-mDrGJwW4fiH5zVd4SaHih7PObxP_0',
  'Ef9KkdMtAom9qYE64A_3ZA5sOP3OduRYPdavxGO3DH12fF5g',
  'Ef9-8keOeXR4Sn-ywrlFgxma4ubJvEFRW3jgP0ib16A-HCiG',
  DEFAULT_NOMINATORS_STAKING_POOL,
  'Ef_CbvHoa5imR1x_ESkUT_6NJQoONbSGp8MkrAu1xtM6NOxE',
  'Ef-j7wmnLdy54kZC0gtbVbCrdPA4cFLr3rxLOoDcpzR_SyBX',
];

export const STAKING_POOLS = [
  ...(process.env.STAKING_POOLS ? process.env.STAKING_POOLS.split(' ') : []),
  ...DEFAULT_STAKING_POOLS,
].filter(Boolean);
export const LIQUID_POOL = process.env.LIQUID_POOL || 'EQD2_4d91M4TVbEBVyBF8J1UwpMJc361LKVCz6bBlffMW05o';
export const LIQUID_JETTON = process.env.LIQUID_JETTON || 'EQCqC6EhRJ_tpWngKxL6dV0k6DSnRUrs9GSVkLbfdCqsj6TE';
export const STAKING_MIN_AMOUNT = ONE_TON;
export const NOMINATORS_STAKING_MIN_AMOUNT = 10_000n * ONE_TON;
export const MIN_ACTIVE_STAKING_REWARDS = 100_000_000n; // 0.1 MY
// Staked tokens now showing with all other tokens, so we need to add a prefix to avoid collisions
export const STAKING_SLUG_PREFIX = 'staking-';

export const TONCONNECT_PROTOCOL_VERSION = 2;
export const TONCONNECT_WALLET_JSBRIDGE_KEY = IS_CORE_WALLET ? 'tonwallet' : 'mytonwallet';
export const EMBEDDED_DAPP_BRIDGE_CHANNEL = 'embedded-dapp-bridge';

export const NFT_FRAGMENT_COLLECTIONS = [
  '0:0e41dc1dc3c9067ed24248580e12b3359818d83dee0304fabcf80845eafafdb2', // Anonymous Telegram Numbers
  '0:80d78a35f955a14b679faa887ff4cd5bfc0f43b4a4eea2a7e6927f3701b273c2', // Telegram Usernames
];
export const NFT_FRAGMENT_GIFT_IMAGE_TO_URL_REGEX = /^https?:\/\/nft\.(fragment\.com\/gift\/[\w-]+-\d+)\.\w+$/i;
export const TELEGRAM_GIFTS_SUPER_COLLECTION = 'super:telegram-gifts';

export const MW_CARDS_WEBSITE = 'https://cards.mytonwallet.io';
export const MW_CARDS_COLLECTION = 'EQCQE2L9hfwx1V8sgmF9keraHx1rNK9VmgR1ctVvINBGykyM';

export const TON_DNS_RENEWAL_WARNING_DAYS = 14;
export const TON_DNS_RENEWAL_NFT_WARNING_DAYS = 30;

export const TONCOIN = {
  name: 'Gram',
  symbol: 'GRAM',
  slug: 'toncoin',
  decimals: 9,
  chain: 'ton',
  cmcSlug: 'toncoin',
  priceUsd: 1.5,
} as const;

export const TRX = {
  name: 'Tron',
  symbol: 'TRX',
  slug: 'trx',
  decimals: 6,
  chain: 'tron',
  cmcSlug: 'tron',
} as const;

export const SOLANA = {
  name: 'Solana',
  symbol: 'SOL',
  slug: 'sol',
  decimals: 9,
  chain: 'solana',
  cmcSlug: 'solana',
} as const;

export const ETH = {
  name: 'Ethereum',
  symbol: 'ETH',
  slug: 'eth',
  decimals: 18,
  chain: 'ethereum',
} as const;

export const BASE = {
  name: 'Base',
  symbol: 'ETH',
  slug: 'base',
  decimals: 18,
  chain: 'base',
  label: 'Base',
} as const;

export const BNB = {
  name: 'BNB',
  symbol: 'BNB',
  slug: 'bnb',
  decimals: 18,
  chain: 'bnb',
} as const;

export const POLYGON = {
  name: 'Polygon',
  symbol: 'POL',
  slug: 'pol',
  decimals: 18,
  chain: 'polygon',
} as const;

export const ARBITRUM = {
  name: 'Arbitrum',
  symbol: 'ETH',
  slug: 'arb',
  decimals: 18,
  chain: 'arbitrum',
  label: 'Arbitrum',
} as const;

export const MONAD = {
  name: 'Monad',
  symbol: 'MON',
  slug: 'mon',
  decimals: 18,
  chain: 'monad',
} as const;

export const AVALANCHE = {
  name: 'Avalanche',
  symbol: 'AVAX',
  slug: 'ava',
  decimals: 18,
  chain: 'avalanche',
} as const;

export const HYPERLIQUID = {
  name: 'Hyperliquid',
  symbol: 'HYPE',
  slug: 'hyperliquid',
  decimals: 18,
  chain: 'hyperliquid',
} as const;

export const ROBINHOOD = {
  name: 'Robinhood',
  symbol: 'ETH',
  slug: 'robinhood',
  decimals: 18,
  chain: 'robinhood',
  label: 'Robinhood',
} as const;

export const MYCOIN_MAINNET = {
  name: 'My Wallet Coin',
  symbol: 'MY',
  slug: 'ton-eqcfvnlrbn',
  decimals: 9,
  chain: 'ton',
  minterAddress: 'EQCFVNlRb-NHHDQfv3Q9xvDXBLJlay855_xREsq5ZDX6KN-w',
  image: 'https://mytonwallet.io/logo-256-blue.png',
} as const;

export const MYCOIN_TESTNET = {
  ...MYCOIN_MAINNET,
  slug: 'ton-kqawlxpebw',
  minterAddress: 'kQAWlxpEbwhCDFX9gp824ee2xVBhAh5VRSGWfbNFDddAbQoQ',
  image: undefined,
} as const;

export const STAKED_TON_SLUG = 'ton-eqcqc6ehrj';
export const STAKED_MYCOIN_SLUG = 'ton-eqcbzvsfwq';
export const MYCOIN_STAKING_POOL = 'EQC3roTiRRsoLzfYVK7yVVoIZjTEqAjQU3ju7aQ7HWTVL5o5';

// Tokens that do not accept new stakes; existing positions stay fully withdrawable
export const NEW_STAKE_DISABLED_TOKEN_SLUGS: ReadonlySet<string> = new Set([MYCOIN_MAINNET.slug, MYCOIN_TESTNET.slug]);

export const ETHENA_STAKING_VAULT = 'EQChGuD1u0e7KUWHH5FaYh_ygcLXhsdG2nSHPXHW8qqnpZXW';
export const ETHENA_STAKING_MIN_AMOUNT = 1_000_000; // 1 USDe
export const ETHENA_ELIGIBILITY_CHECK_URL = 'https://t.me/id_app/start?startapp=cQeewNnc3pVphUcwY63WruKMQDpgePd1E7eMVoqphMZAdGoU9jwS4qRqrM1kSeaqrAiiDiC3EYAJPwZDGWqxZpw5vtGxmHma59XEt';

export const STON_PTON_ADDRESS = 'EQCM3B12QK1e4yZSf8GtBRT0aLMNyEsBc_DhVfRRtOEffLez';
export const STON_PTON_SLUG = 'ton-eqcm3b12qk';

export const DNS_IMAGE_GEN_URL = 'https://dns-image.mytonwallet.org/img?d=';

export const TRC20_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USDT',
  decimals: 6,
  chain: 'tron',
  slug: 'tron-tr7nhqjekq',
  tokenAddress: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
  label: 'TRC-20',
} as const;

export const TRC20_USDT_TESTNET = {
  ...TRC20_USDT_MAINNET,
  ...(IS_LEGENDS_WALLET ? {
    slug: 'tron-txyzopyrdj',
    tokenAddress: 'TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf',
  } : {
    slug: 'tron-tg3xxyexbk',
    tokenAddress: 'TG3XXyExBkPp9nzdajDZsozEu4BkaSJozs',
  }),
};

export const TRC20_BTT_TESTNET = {
  name: 'BitTorrent',
  symbol: 'BTT',
  decimals: 18,
  chain: 'tron',
  slug: 'tron-tnuokl1ni8',
  tokenAddress: 'TNuoKL1ni8aoshfFL1ASca1Gou9RXwAzfn',
  label: 'TRC-20',
} as const;

export const TON_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USD₮',
  chain: 'ton',
  slug: 'ton-eqcxe6mutq',
  decimals: 6,
  tokenAddress: 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
  image: 'https://imgproxy.mytonwallet.org/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  label: 'TON',
  priceUsd: 1,
} as const;

// Where to get this token: https://t.me/testgiver_ton_usdt_bot
export const TON_USDT_TESTNET = {
  ...TON_USDT_MAINNET,
  slug: 'ton-kqd0gkbm8z',
  tokenAddress: 'kQD0GKBM8ZbryVk2aESmzfU6b9b_8era_IkvBSELujFZPsyy',
  image: undefined,
} as const;

export const TON_USDE = {
  name: 'Ethena USDe',
  symbol: 'USDe',
  chain: 'ton',
  tokenAddress: 'EQAIb6KmdfdDR7CN1GBqVJuP25iCnLKCvBlJ07Evuu2dzP5f',
  slug: 'ton-eqaib6kmdf',
  decimals: 6,
  image: 'https://imgproxy.toncenter.com/binMwUmcnFtjvgjp4wSEbsECXwfXUwbPkhVvsvpubNw/pr:small/aHR0cHM6Ly9tZXRhZGF0YS5sYXllcnplcm8tYXBpLmNvbS9hc3NldHMvVVNEZS5wbmc',
} as const;

export const TON_TSUSDE = {
  name: 'Ethena tsUSDe',
  symbol: 'tsUSDe',
  chain: 'ton',
  tokenAddress: 'EQDQ5UUyPHrLcQJlPAczd_fjxn8SLrlNQwolBznxCdSlfQwr',
  slug: 'ton-eqdq5uuyph',
  decimals: 6,
  image: 'https://cache.tonapi.io/imgproxy/vGZJ7erwsWPo7DpVG_V7ygNn7VGs0szZXcNLHB_l0ms/rs:fill:200:200:1/g:no/aHR0cHM6Ly9tZXRhZGF0YS5sYXllcnplcm8tYXBpLmNvbS9hc3NldHMvdHNVU0RlLnBuZw.webp',
} as const;

export const SOLANA_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USDT',
  decimals: 6,
  chain: 'solana',
  slug: 'solana-es9vmfrzac',
  tokenAddress: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
  label: 'SOL',
  image: 'https://imgproxy.mytonwallet.org/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  priceUsd: 1,
} as const;

export const SOLANA_USDC_MAINNET = {
  name: 'USD Coin',
  symbol: 'USDC',
  decimals: 6,
  chain: 'solana',
  slug: 'solana-epjfwdd5au',
  tokenAddress: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  label: 'SOL',
  image: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  priceUsd: 1,
} as const;

export const ETH_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USDT',
  decimals: 6,
  chain: 'ethereum',
  slug: 'ethereum-0xdac17f95',
  tokenAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  label: 'ERC-20',
  image: 'https://imgproxy.mytonwallet.org/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  priceUsd: 1,
} as const;

export const ETH_USDC_MAINNET = {
  name: 'USD Coin',
  symbol: 'USDC',
  decimals: 6,
  chain: 'ethereum',
  slug: 'ethereum-0xa0b86991',
  tokenAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  label: 'ERC-20',
  image: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  priceUsd: 1,
} as const;

export const BASE_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USDT',
  decimals: 6,
  chain: 'base',
  slug: 'base-0xfde4c96c',
  tokenAddress: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
  label: 'ERC-20',
  image: 'https://imgproxy.mytonwallet.org/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  priceUsd: 1,
} as const;

export const BASE_USDC_MAINNET = {
  name: 'USD Coin',
  symbol: 'USDC',
  decimals: 6,
  chain: 'base',
  slug: 'base-0x833589fc',
  tokenAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  label: 'ERC-20',
  image: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  priceUsd: 1,
} as const;

export const ARBITRUM_USDC_MAINNET = {
  name: 'USD Coin',
  symbol: 'USDC',
  decimals: 6,
  chain: 'arbitrum',
  slug: 'arbitrum-0xaf88d065',
  tokenAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  label: 'ERC-20',
  image: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  priceUsd: 1,
} as const;

export const BSC_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USDT',
  decimals: 18,
  chain: 'bnb',
  slug: 'bnb-0x55d39832',
  tokenAddress: '0x55d398326f99059ff775485246999027b3197955',
  label: 'BEP-20',
  image: 'https://imgproxy.mytonwallet.org/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  priceUsd: 1,
} as const;

export const AVALANCHE_USDT_MAINNET = {
  name: 'Tether USD',
  symbol: 'USDT',
  decimals: 6,
  chain: 'avalanche',
  slug: 'avalanche-0x9702230a',
  tokenAddress: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
  label: 'ERC-20',
  image: 'https://imgproxy.mytonwallet.org/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  priceUsd: 1,
} as const;

export const HYPERLIQUID_USDC_MAINNET = {
  name: 'USD Coin',
  symbol: 'USDC',
  decimals: 6,
  chain: 'hyperliquid',
  slug: 'hyperliquid-0xb88339cb',
  tokenAddress: '0xb88339CB7199b77E23DB6E890353E22632Ba630f',
  label: 'ERC-20',
  image: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  priceUsd: 1,
} as const;

/** The properties not returned by the backend, and therefore not stored in token objects */
export const TOKEN_CUSTOM_STYLES: Partial<Record<string, {
  fontIcon?: string;
  cardColor?: keyof typeof TOKEN_CARD_COLORS;
}>> = {
  [TONCOIN.slug]: {
    fontIcon: 'icon-chain-ton',
    cardColor: 'blue',
  },
  [TRX.slug]: {
    fontIcon: 'icon-chain-tron',
    cardColor: 'red',
  },
  [SOLANA.slug]: {
    fontIcon: 'icon-chain-solana',
    cardColor: 'purple',
  },
  [ETH.slug]: {
    fontIcon: 'icon-chain-ethereum',
    cardColor: 'purple',
  },
  [BASE.slug]: {
    fontIcon: 'icon-chain-base',
    cardColor: 'blue',
  },
  [ROBINHOOD.slug]: {
    fontIcon: 'icon-chain-robinhood',
    cardColor: 'green',
  },
  [STAKED_TON_SLUG]: {
    cardColor: 'green',
  },
};

export const ALL_STAKING_POOLS = [
  LIQUID_POOL,
  ...DEFAULT_STAKING_POOLS,
  MYCOIN_STAKING_POOL,
  ETHENA_STAKING_VAULT,
  TON_TSUSDE.tokenAddress,
];

// Native tokens in the UI display order (see CHAIN_DISPLAY_ORDER). Drives the empty-wallet token order.
export const PRIORITY_TOKENS = [
  ...(IS_LEGENDS_WALLET ? [TRX] : [
    ETH,
    SOLANA,
    HYPERLIQUID,
    TONCOIN,
    TRX,
    BNB,
    BASE,
    ROBINHOOD,
    MONAD,
    ARBITRUM,
    POLYGON,
    AVALANCHE,
  ]),
] as ApiToken[];

export const INIT_SWAP_ASSETS: Record<'in' | 'out', ApiSwapAsset> = {
  in: {
    ...TONCOIN,
    isPopular: true,
  },
  out: {
    ...TON_USDT_MAINNET,
    isPopular: true,
  },
};

export const DEFAULT_SWAP_FIRST_TOKEN_SLUG = TONCOIN.slug;
export const DEFAULT_SWAP_SECOND_TOKEN_SLUG = TON_USDT_MAINNET.slug;
export const DEFAULT_SWAP_AMOUNT = '10';
export const DEFAULT_TRANSFER_TOKEN_SLUG = IS_TRON_ONLY ? TRX.slug : TONCOIN.slug;

export const SWAP_DEX_LABELS: Record<ApiSwapDexLabel, string> = {
  dedust: 'DeDust',
  ston: 'STON.fi',
};

export const ACTIVE_TAB_STORAGE_KEY = IS_CORE_WALLET
  ? 'tw-active-tab'
  : IS_EXPLORER
    ? 'explorer-active-tab'
    : 'mtw-active-tab';

export const INDEXED_DB_NAME = IS_EXPLORER ? 'explorer-keyval-store' : 'keyval-store';
export const INDEXED_DB_STORE_NAME = 'keyval';

export const WINDOW_PROVIDER_CHANNEL = 'windowProvider';
export const WINDOW_PROVIDER_PORT = `${IS_CORE_WALLET ? 'TonWallet' : 'MyWallet'}_popup_reversed`;

export const SHOULD_SHOW_ALL_ASSETS_AND_ACTIVITY = IS_FEATURE_LIMITED;
export const PORTRAIT_MIN_ASSETS_TAB_VIEW = 6;

export const DEFAULT_PRICE_CURRENCY = 'USD';
export const CURRENCIES: Record<
  ApiBaseCurrency,
  // Fallbacks are used until the Legends wallet backend returns current rates.
  { name: string; decimals: number; shortSymbol?: string; shortSymbolPosition?: 'start' | 'end'; fallbackRate: string }
> = {
  USD: {
    name: 'US Dollar',
    decimals: 2,
    shortSymbol: '$',
    fallbackRate: '1',
  },
  EUR: {
    name: 'Euro',
    decimals: 2,
    shortSymbol: '€',
    fallbackRate: '0.85233500',
  },
  RUB: {
    name: 'Russian Ruble',
    decimals: 2,
    shortSymbol: '₽',
    fallbackRate: '84.49824600',
  },
  CNY: {
    name: 'Chinese Yuan',
    decimals: 2,
    shortSymbol: '¥',
    fallbackRate: '7.11865000',
  },
  BTC: {
    name: 'Bitcoin',
    decimals: 9,
    fallbackRate: '0.00000866',
  },
  TON: {
    name: 'Gram',
    decimals: 9,
    shortSymbol: 'GRAM',
    shortSymbolPosition: 'end',
    fallbackRate: '0.31360000',
  },
};

export const BURN_ADDRESS = 'UQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJKZ';

export const DEFAULT_WALLET_VERSION: ApiTonWalletVersion = 'W5';
export const POPULAR_WALLET_VERSIONS: readonly ApiTonWalletVersion[] = ['v3R1', 'v3R2', 'v4R2', 'W5'];

export const DEFAULT_TIMEOUT = 10000;
export const DEFAULT_RETRIES = 3;
export const DEFAULT_ERROR_PAUSE = 500;

export const BROWSER_HISTORY_LIMIT = 10;

export const NFT_BATCH_SIZE = 4;
export const NOTCOIN_VOUCHERS_ADDRESS = 'EQDmkj65Ab_m0aZaW8IpKw4kYqIgITw_HRstYEkVQ6NIYCyW';
export const BURN_CHUNK_DURATION_APPROX_SEC = 30;
export const NOTCOIN_FORWARD_TON_AMOUNT = 30000000n; // 0.03 TON
export const NOTCOIN_EXCHANGERS = [
  'EQAPZauWVPUcm2hUJT9n36pxznEhl46rEn1bzBXN0RY_yiy2',
  'EQASgm0Qv3h2H2mF0W06ikPqYq2ctT3dyXMJH_svbEKKB3iZ',
  'EQArlmP-RhVIG2yAFGZyPZfM3m0YccxmpvoRi6sgRzWnAA0s',
  'EQA6pL-spYqZp1Ck6o3rpY45Cl-bvLMW_j3qdVejOkUWpLnm',
  'EQBJ_ehYjumQKbXfWUue1KHKXdTm1GuYJB0Fj2ST_DwORvpd',
  'EQBRmYSjxh9xlZpUqEmGjF5UjukI9v_Cm2kCTu4CoBn3XkOD',
  'EQBkiqncd7AFT5_23H-RoA2Vynk-Nzq_dLoeMVRthAU9RF0p',
  'EQB_OzTHXbztABe0QHgr4PtAV8T64LR6aDunXgaAoihOdxwO',
  'EQCL-x5kLg6tKVNGryItTuj6tG3FH5mhUEu0xRqQc-kbEmbe',
  'EQCZh2yJ46RaQH3AYmjEA8SMMXi77Oein4-3lvqkHseIAhD-',
  'EQChKo5IK3iNqUHUGDB9gtzjCjMTPtmsFqekuCA2MdreVEyu',
  'EQC6DNCBv076TIliRMfOt20RpbS7rNKDfSky3WrFEapFt8AH',
  'EQDE_XFZOYae_rl3ZMsgBCtRSmYhl8B4y2BZEP7oiGBDhlgy',
  'EQDddqpGA2ePXQF47A2DSL3GF6ZzIVmimfM2d16cdymy2noT',
  'EQDv0hNNAamhYltCh3pTJrq3oRB9RW2ZhEYkTP6fhj5BtZNu',
  'EQD2mP7zgO7-imUJhqYry3i07aJ_SR53DaokMupfAAobt0Xw',
] as const;

export const CLAIM_ADDRESS = 'EQB3zOTvPi1PmwdcTpqSfFKZnhi1GNKEVJM-LdoAirdLtash';
export const CLAIM_AMOUNT = 30000000n; // 0.03 TON
export const CLAIM_COMMENT = 'claim';

export const MINT_CARD_ADDRESS = 'EQBpst3ZWJ9Dqq5gE2YH-yPsFK_BqMOmgi7Z_qK6v7WbrPWv';
export const MINT_CARD_COMMENT = 'Mint card';
export const MINT_CARD_REFUND_COMMENT = 'Refund';

export const RE_LINK_TEMPLATE = /((ftp|https?):\/\/)?(?<host>(www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z][-a-zA-Z0-9]{1,62})\b([-a-zA-Z0-9()@:%_+.,~#?&/=]*)/g;

export const RE_TG_BOT_MENTION = /(telegram|tg)[:\s-]*@[a-z0-9_]+|(https?:\/\/)?(t\.me|telegram\.me|telegram\.dog)\/[a-z0-9_]+/mi;

export const STARS_SYMBOL = '⭐️';

export const AUTOLOCK_OPTIONS_LIST = [
  {
    value: 'never',
    name: 'Disabled',
    selectedName: 'Disabled',
    period: 0,
  },
  {
    value: '1',
    name: '30 seconds',
    selectedName: 'If away for 30 sec',
    period: 30_000,
  },
  {
    value: '2',
    name: '3 minutes',
    selectedName: 'If away for 3 min',
    period: 60_000 * 3,
  },
  {
    value: '3',
    name: '10 minutes',
    selectedName: 'If away for 10 min',
    period: 60_000 * 10,
  },
] as const;

export const AUTO_CONFIRM_DURATION_MINUTES = 5;

export const PRICELESS_TOKEN_HASHES = new Set([
  '173e31eee054cb0c76f77edc7956bed766bf48a1f63bd062d87040dcd3df700f', // FIVA SY tsTON EQAxGi9Al7hamLAORroxGkvfap6knGyzI50ThkP3CLPLTtOZ
  '5226dd4e6db9af26b24d5ca822bc4053b7e08152f923932abf25030c7e38bb42', // FIVA PT tsTON EQAkxIRGXgs2vD2zjt334MBjD3mXg2GsyEZHfzuYX_trQkFL
  'fea2c08a704e5192b7f37434927170440d445b87aab865c3ea2a68abe7168204', // FIVA YT tsTON EQAcy60qg22RCq87A_qgYK8hooEgjCZ44yxhdnKYdlWIfKXL
  'e691cf9081a8aeb22ed4d94829f6626c9d822752e035800b5543c43f83d134b5', // FIVA LP tsTON EQD3BjCjxuf8mu5kvxajVbe-Ila1ScZZlAi03oS7lMmAJjM3
  '301ce25925830d713b326824e552e962925c4ff45b1e3ea21fc363a459a49b43', // FIVA SY eUSDT EQDi9blCcyT-k8iMpFMYY0t7mHVyiCB50ZsRgyUECJDuGvIl
  '02250f83fbb8624d859c2c045ac70ee2b3b959688c3d843aec773be9b36dbfc3', // FIVA PT eUSDT EQBzVrYkYPHx8D_HPfQacm1xONa4XSRxl826vHkx_laP2HOe
  'dba3adb2c917db80fd71a6a68c1fc9e12976491a8309d5910f9722efc084ce4d', // FIVA YT eUSDT EQCwUSc2qrY5rn9BfFBG9ARAHePTUvITDl97UD0zOreWzLru
  '7da9223b90984d6a144e71611a8d7c65a6298cad734faed79438dc0f7a8e53d1', // FIVA LP eUSDT EQBNlIZxIbQGQ78cXgG3VRcyl8A0kLn_6BM9kabiHHhWC4qY
  'ddf80de336d580ab3c11d194f189c362e2ca1225cae224ea921deeaba7eca818', // tsUSDe EQDQ5UUyPHrLcQJlPAczd_fjxn8SLrlNQwolBznxCdSlfQwr
  'eb9d9891a32ec94425c09735f6ade73f4c171da0091f874d6e9d25247d583990', // Affluent TON Lending Vault EQADQ6JcK0NMuNM5uwCcS9bjcn2RTvcxYIZjNlhIhywUrfBN
  'f66c149de251ffd031bdb34b79abe43a062ba16b815433691e3ec40a77f01d71', // Affluent Ethena Multiply Vault EQDXmtbt1-WSP00tSh6N6FH-4lX7LbnrjORClmtmuZqg4Ymm
  'bca42dbdcbc0d885aaffb1eeeb027d9f338c2dd68701a05641c1d1c3171a7400', // Affluent TON Multiply Vault EQDtxQqkgIRQQR5hWlrQxiJMtLwjR3rEYNUBbEcvPDwCs1Ng
]);

export const STAKED_TOKEN_SLUGS = new Set([
  STAKED_TON_SLUG,
  STAKED_MYCOIN_SLUG,
  TON_TSUSDE.slug,
]);

export const DEFAULT_OUR_SWAP_FEE = 0.875;
export const MW_AGGREGATOR_QUERY_ID = '4246015164496276000';

export const DEFAULT_STAKING_STATE: ApiLiquidStakingState = {
  type: 'liquid',
  id: 'liquid',
  tokenSlug: TONCOIN.slug,
  annualYield: 14.09,
  yieldType: 'APY',
  balance: 0n,
  pool: LIQUID_POOL,
  tokenBalance: 0n,
  unstakeRequestAmount: 0n,
  instantAvailable: 0n,
  start: 0,
  end: 0,
  tvl: 0n,
  totalStakers: 0,
};

export const DEFAULT_NOMINATORS_STAKING_STATE: ApiNominatorsStakingState = {
  type: 'nominators',
  id: 'nominators',
  tokenSlug: TONCOIN.slug,
  annualYield: 10.37,
  yieldType: 'APY',
  balance: 0n,
  pool: LEGACY_NOMINATORS_STAKING_POOL,
  start: 0,
  end: 0,
};

export const SWAP_API_VERSION = 3;
export const TONCENTER_ACTIONS_VERSION = 'v1';

export const JVAULT_URL = 'https://jvault.xyz';

export const HELP_CENTER_URL = {
  home: {
    en: 'https://legends.energy/',
    ru: 'https://legends.energy/',
  },
  domainScam: {
    en: 'https://help.mywallet.io/intro/scams/.ton-domain-scams',
    ru: 'https://help.mywallet.io/ru/baza-znanii/moshennichestvo-i-skamy/moshennichestvo-s-ispolzovaniem-domenov-.ton',
  },
  seedScam: {
    en: 'https://help.mywallet.io/intro/scams/leaked-seed-phrases',
    ru: 'https://help.mywallet.io/ru/baza-znanii/moshennichestvo-i-skamy/slitye-sid-frazy',
  },
  ethenaStaking: {
    en: 'https://help.mywallet.io/intro/staking/what-is-usde-how-does-the-ethena-protocol-work',
    ru: 'https://help.mywallet.io/ru/baza-znanii/steiking/chto-takoe-usde-kak-rabotaet-protokol-ethena',
  },
};

const ALL_TON_DNS_ZONES = [
  {
    suffixes: ['ton'],
    baseFormat: /^([-\da-z]+\.){0,2}[-\da-z]{4,126}$/i,
    resolver: 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz',
    collectionName: 'TON DNS Domains',
    isUnofficial: false,
    isRenewable: true,
    isLinkable: true,
    isTelemint: false,
  },
  {
    suffixes: ['t.me'],
    baseFormat: /^([-\da-z]+\.){0,2}[-_\da-z]{4,32}$/i,
    resolver: 'EQCA14o1-VWhS2efqoh_9M1b_A9DtKTuoqfmkn83AbJzwnPi',
    collectionName: 'Telegram Usernames',
    isUnofficial: false,
    isRenewable: false,
    isLinkable: true,
    isTelemint: true,
  },
  {
    suffixes: ['vip', 'ton.vip', 'vip.ton'],
    baseFormat: /^([-\da-z]+\.){0,2}[\da-z]{1,24}$/i,
    resolver: 'EQBWG4EBbPDv4Xj7xlPwzxd7hSyHMzwwLB5O6rY-0BBeaixS',
    collectionName: 'VIP DNS Domains',
    isUnofficial: true,
    isRenewable: false,
    isLinkable: true,
    isTelemint: false,
  },
  {
    suffixes: ['grm'],
    baseFormat: /^([-\da-z]+\.){0,2}[-\da-z]{1,127}$/i,
    resolver: 'EQAic3zPce496ukFDhbco28FVsKKl2WUX_iJwaL87CBxSiLQ',
    collectionName: 'GRAM DNS Domains',
    isUnofficial: true,
    isRenewable: false,
    isLinkable: true,
    isTelemint: false,
  },
] as const;

export const TON_DNS_ZONES = IS_FEATURE_LIMITED
  ? ALL_TON_DNS_ZONES.filter(({ isUnofficial }) => !isUnofficial)
  : ALL_TON_DNS_ZONES;

export const RENEWABLE_TON_DNS_COLLECTIONS = new Set<string>(
  TON_DNS_ZONES.filter((zone) => zone.isRenewable).map((zone) => zone.resolver),
);

export const DEFAULT_AUTOLOCK_OPTION: AutolockValueType = '3';
export const WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION = 2;

export const UNKNOWN_TOKEN = {
  symbol: '[Unknown]',
  decimals: 9,
} as const;

export const DEFAULT_CHAIN: ApiChain = IS_LEGENDS_WALLET ? 'tron' : 'ton';

export const MFA_BOT_URL = process.env.MFA_BOT_URL || 'https://t.me/tgmfabot/auth';
export const MFA_API_BASE_URL = process.env.MFA_API_BASE_URL || 'https://mfa-server.mytonwallet.org';
export const MFA_MASTER_ADDRESS = 'UQCIoyc951J4hQwboW1-Gbt0kK0z920N2y8GbNXqCzWqe2ds';
export const MFA_EXTENSION_CODE_HASH = '701eede652337f699550cc51cb15263259aae6fc6eba976237945f142dda982d';
